import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type {
  DeliveryResultReportDetail,
  GetDeliveryResultReportDetailParams,
  GetDeliveryResultReportDetailResponse,
} from '~/types/delivery-agency.types';

/**
 * [배송 결과 리포트] 상세 조회 API
 */
const fetchDeliveryResultReportDetail = async (
  params: GetDeliveryResultReportDetailParams
): Promise<DeliveryResultReportDetail> => {
  const { data } = await axiosInstance.get<GetDeliveryResultReportDetailResponse>(
    `${API_ENDPOINT.DELIVERY_AGENCY.GET_DELIVERY_RESULT_REPORT_DETAIL}/${params.reportId}`
  );
  return data.data;
};

/**
 * [배송 결과 리포트] 상세 조회 Mutation Hook
 */
export const useGetDeliveryResultReportDetailMutation = (options?: { dimmed?: boolean }) => {
  return useMutation({
    mutationFn: fetchDeliveryResultReportDetail,
    meta: { dimmed: options?.dimmed ?? true },
  });
};

/**
 * [배송 결과 리포트] 상세 조회 Mock
 */
registerMockAPI<GetDeliveryResultReportDetailResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/delivery-result-reports/:id(\\d+)',
  label: '배송 결과 리포트/상세',
  scenarios: [
    {
      name: '배송완료 (사진 있음)',
      response: {
        data: {
          shippingLabelNumber: '210-C0-JA9LAK0133660-0001',
          courierCode: { code: 'NM', description: '넥스트마일' },
          reportedShippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
          reportedDeliveryResults: [{ code: 'NORMAL', description: '정상배송' }],
          actualDeliveryLocationType: '문 앞',
          deliveryCompletedImageUrl: 'https://picsum.photos/seed/kls-report/360/480',
        },
      },
    },
    {
      name: '배송완료 (사진 로드 실패)',
      response: {
        data: {
          shippingLabelNumber: '210-C0-JA9LAK0133662-0001',
          courierCode: { code: 'NM', description: '넥스트마일' },
          reportedShippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
          reportedDeliveryResults: [{ code: 'NORMAL', description: '정상배송' }],
          actualDeliveryLocationType: '문 앞',
          deliveryCompletedImageUrl: 'https://invalid.example.com/broken-image.jpg',
        },
      },
    },
    {
      name: '배송실패 (사진 없음)',
      response: {
        data: {
          shippingLabelNumber: '210-C0-JA9LAK0133661-0001',
          courierCode: { code: 'CJT', description: 'CJ' },
          reportedShippingLabelStatus: { code: 'DELIVERY_FAILED', description: '배송 실패' },
          reportedDeliveryResults: [{ code: 'NON_DELIVERED', description: '미배송' }],
          actualDeliveryLocationType: '',
          deliveryCompletedImageUrl: null,
        },
      },
    },
  ],
});
